import { CreditCard, Receipt, Hash, TrendingDown } from 'lucide-react';
import { fmtMoney, fmtNumber, fmtPct } from './format';

/**
 * TotalsBar — the bottom-line for the CURRENT FILTER SELECTION.
 *
 * Every number here comes from the server's aggregate over the whole filtered
 * set, never from the rows rendered on screen — with pagination those differ,
 * and the page-sum would silently under-report. See buildFilters() in
 * backend/src/routes/expenses.js.
 *
 * Sticky so the bottom line stays visible while scrolling a long transaction
 * table, which is the whole point of it.
 */
export default function TotalsBar({ totals, shareOfTotal, description, loading }) {
  if (!totals) return null;

  const hasCredits = Number(totals.credit_amount) < 0;

  return (
    <div style={{ ...s.bar, opacity: loading ? 0.55 : 1 }}>
      <div style={s.left}>
        <div style={s.scopeLabel}>Filtered total</div>
        <div style={s.scopeDesc} title={description}>{description}</div>
      </div>

      <div style={s.netBlock}>
        <div style={s.netLabel}>NET</div>
        <div style={s.netValue}>{fmtMoney(totals.net_amount)}</div>
        {shareOfTotal !== undefined && shareOfTotal < 0.9999 && (
          <div style={s.share}>{fmtPct(shareOfTotal)} of all spend</div>
        )}
      </div>

      <div style={s.stats}>
        <Stat icon={<Hash size={13} />} label="Transactions" value={fmtNumber(totals.transaction_count)}
              sub={`avg ${fmtMoney(totals.average_amount)}`} />
        <Stat icon={<CreditCard size={13} />} label="Company card" value={fmtMoney(totals.card_amount)}
              sub={`${fmtNumber(totals.card_count)} txns`} />
        <Stat icon={<Receipt size={13} />} label="Reimbursed" value={fmtMoney(totals.reimbursement_amount)}
              sub={`${fmtNumber(totals.reimbursement_count)} txns`} />
        {/* Only shown when the selection actually contains negatives, so the
            bar stays quiet in the common case but never hides a netted-away
            credit when there is one. */}
        {hasCredits && (
          <Stat icon={<TrendingDown size={13} />} label="Credits" value={fmtMoney(totals.credit_amount)}
                sub={`gross ${fmtMoney(totals.gross_amount)}`} accent="#B45309" />
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, accent }) {
  return (
    <div style={s.stat}>
      <div style={s.statLabel}><span style={{ opacity: 0.6 }}>{icon}</span>{label}</div>
      <div style={{ ...s.statValue, color: accent || '#E8EEF7' }}>{value}</div>
      {sub && <div style={s.statSub}>{sub}</div>}
    </div>
  );
}

const s = {
  bar: {
    position: 'sticky', bottom: 0, zIndex: 40,
    display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
    padding: '12px 18px', marginTop: '16px',
    background: '#1B2A4A', borderRadius: '10px',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.18)',
    transition: 'opacity 0.15s',
  },
  left:      { minWidth: 0, flex: '1 1 190px' },
  scopeLabel:{ fontSize: '9px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#7A90B3' },
  scopeDesc: { fontSize: '11px', color: '#94A8C0', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  netBlock:  { paddingRight: '20px', borderRight: '1px solid rgba(255,255,255,0.12)' },
  netLabel:  { fontSize: '9px', fontWeight: 700, letterSpacing: '1.2px', color: '#7A90B3' },
  netValue:  { fontSize: '24px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
  share:     { fontSize: '10px', color: '#7A90B3', marginTop: '1px' },
  stats:     { display: 'flex', gap: '22px', flexWrap: 'wrap', flex: '2 1 auto' },
  stat:      { minWidth: '96px' },
  statLabel: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#7A90B3' },
  statValue: { fontSize: '14px', fontWeight: 600, marginTop: '2px', fontVariantNumeric: 'tabular-nums' },
  statSub:   { fontSize: '10px', color: '#7A90B3', marginTop: '1px' },
};
